import React from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectVideoShowScreenShareInGrid } from 'flow/selectors/video-selectors';
import { MeetingMemberGuestBadge } from 'flow/selectors/meeting-selectors';
import NamePlate from './NamePlate';
import StatusIconPlate from './StatusIconPlate';
import RestartVideoPlate from './RestartVideoPlate';
import TileVideoStatsPlate from './TileVideoStatsPlate';

type RemoteVideoLabelsProps = {
    displayName: string | null;
    /** Live in the call but not a permanent chat room member — null for room members / self. */
    guestBadge?: MeetingMemberGuestBadge;
    isCameraFeed: boolean;
    isCaller: boolean;
    playAudio: boolean;
    showVideo: boolean;
    peerSetupAttempts: number;
    isCompact: boolean;
    onRestartVideo: () => void;
    /**
     * Signaling name of the feed's publisher, used to look up its debug stats
     * (VID-396). MUST be null for a SECONDARY (camera-during-screen-share)
     * feed: stats are keyed by member name and collected for the primary leg
     * only, so passing the name here would render the screen-share leg's
     * numbers on the camera tile — the lookup returns non-null, just for the
     * wrong feed. (Dial-in feeds need no special-casing; they have no receiver
     * peer, so the lookup is naturally null.)
     */
    statsMemberName?: string | null;
};

type NameLabelPlateProps = Pick<
    RemoteVideoLabelsProps,
    'displayName' | 'guestBadge' | 'isCameraFeed' | 'isCompact'
>;

/** Name plate with the guest badge folded into the label where it applies. */
const NameLabelPlate: React.FC<NameLabelPlateProps> = ({
    displayName,
    guestBadge,
    isCameraFeed,
    isCompact,
}) => {
    const { i18next } = useAppContext();
    const shouldShowScreenShareInGrid = useAppSelector(
        selectVideoShowScreenShareInGrid,
    );

    // The screen-share label ("X's screen") already carries the name in a
    // sentence, so the guest badge is only appended on the plain camera name.
    const guestBadgeLabel =
        isCameraFeed && guestBadge
            ? ` (${i18next.t(
                  guestBadge === 'internal'
                      ? 'meetings.participants.internal_guest'
                      : 'meetings.participants.external_guest',
              )})`
            : '';

    const nameLabel = isCameraFeed
        ? `${displayName || ''}${guestBadgeLabel}`
        : i18next.t('videoconference.screenshare_screen', {
              Name: displayName,
          });

    return (
        <NamePlate
            label={nameLabel}
            isCompact={isCompact}
            hidden={!isCameraFeed && !shouldShowScreenShareInGrid}
        />
    );
};

type StatusPlatesProps = Pick<
    RemoteVideoLabelsProps,
    'isCameraFeed' | 'isCaller' | 'playAudio' | 'showVideo' | 'isCompact'
>;

/** Mic-muted / camera-off / dial-in status icons, in that order. */
const StatusPlates: React.FC<StatusPlatesProps> = ({
    isCameraFeed,
    isCaller,
    playAudio,
    showVideo,
    isCompact,
}) => (
    <>
        {!playAudio && isCameraFeed ? (
            <StatusIconPlate
                iconKey="mic_off"
                isCompact={isCompact}
                testId="meeting-mic-muted"
            />
        ) : null}
        {!showVideo && !isCaller && isCameraFeed ? (
            <StatusIconPlate
                iconKey="videocam_off"
                isCompact={isCompact}
                testId="meeting-video-muted"
            />
        ) : null}
        {isCaller ? (
            <StatusIconPlate
                iconKey="phone"
                isCompact={isCompact}
                testId="meeting-dial-in"
            />
        ) : null}
    </>
);

/**
 * Label plates overlaid on a remote feed.
 *
 * Two groups, so they cannot crowd each other as either grows: name + status
 * icons along the bottom, and the debug stats read-out alone in the top corner.
 * It sits at the `start` corner rather than a hard left one because the pin
 * button already owns `end-2 top-2` — logical sides keep the two apart in RTL
 * too. Anything added to the top later belongs on the `end` side, next to the
 * pin.
 */
const RemoteVideoLabels: React.FC<RemoteVideoLabelsProps> = ({
    displayName,
    guestBadge,
    isCameraFeed,
    isCaller,
    playAudio,
    showVideo,
    peerSetupAttempts,
    isCompact,
    onRestartVideo,
    statsMemberName,
}) => (
    <>
        {statsMemberName ? (
            <div
                className={twMerge(
                    'absolute start-3 top-3 flex gap-2',
                    isCompact ? 'start-1 top-1' : '',
                )}
            >
                <TileVideoStatsPlate
                    memberName={statsMemberName}
                    isCompact={isCompact}
                />
            </div>
        ) : null}
        <div
            className={twMerge(
                // Bound to the tile interior (both insets) so the name plate
                // truncates against the space actually available, not the full
                // tile width.
                'absolute inset-x-3 bottom-3 flex gap-2',
                isCompact ? 'inset-x-1 bottom-1' : '',
            )}
        >
            <NameLabelPlate
                displayName={displayName}
                guestBadge={guestBadge}
                isCameraFeed={isCameraFeed}
                isCompact={isCompact}
            />
            <StatusPlates
                isCameraFeed={isCameraFeed}
                isCaller={isCaller}
                playAudio={playAudio}
                showVideo={showVideo}
                isCompact={isCompact}
            />
            {peerSetupAttempts > 4 && isCameraFeed ? (
                <RestartVideoPlate
                    isCompact={isCompact}
                    onRestart={onRestartVideo}
                />
            ) : null}
        </div>
    </>
);

export default RemoteVideoLabels;
